import { useEffect } from 'react';
import { cn } from '@utils/cn';
import Icon from './Icon';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  isVisible: boolean;
  onClose: () => void;
  duration?: number; // Auto-close duration in ms
}

const Toast = ({
  message,
  type = 'info',
  isVisible,
  onClose,
  duration = 5000,
}: ToastProps) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const typeStyles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  };

  // Use available icons: cart, user, search, star, clock, location, close, plus, minus, check
  const iconMap = {
    success: 'check',
    error: 'close',
    info: 'clock',
    warning: 'clock',
  };

  return (
    <div
      className={cn(
        'fixed top-4 right-4 z-[9998] max-w-md px-4 py-3 rounded-lg border shadow-lg flex items-start gap-3',
        typeStyles[type]
      )}
    >
      <Icon name={iconMap[type] as 'check' | 'close' | 'clock'} className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button
        onClick={onClose}
        className="flex-shrink-0 text-current opacity-70 hover:opacity-100 transition-opacity"
      >
        <Icon name="close" className="w-4 h-4" />
      </button>
    </div>
  );
};

export default Toast;
